import { Component, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { NotificationStateService } from '../../../core/services/notification-state.service';
import { JobStageIssueResponse } from '../../../core/models/job-stage-issue.models';
import { JobPriorityNotificationResponse } from '../../../core/models/job-priority-notification.models';
import { DispatchAlertNotificationResponse } from '../../../core/models/dispatch-alert-notification.models';
import { ResolveJobIssueDialogComponent } from '../components/resolve-job-issue-dialog/resolve-job-issue-dialog.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'issue' | 'priority' | 'dispatch';
  timestamp: Date;
  read: boolean;
  issueData?: JobStageIssueResponse; // Store the original issue data
  priorityData?: JobPriorityNotificationResponse; // Store the original priority notification data
  dispatchData?: DispatchAlertNotificationResponse; // Store the original dispatch alert data
}

@Component({
  selector: 'app-notification-dialog',
  template: `
    <div class="notification-dialog">
      <h2 mat-dialog-title>
        <mat-icon class="notification-icon">notifications</mat-icon>
        Notifications
        <button
          mat-icon-button
          (click)="refreshNotifications()"
          [disabled]="notificationStateService.isLoading$ | async"
          matTooltip="Refresh"
          class="refresh-btn"
        >
          <mat-icon>refresh</mat-icon>
        </button>
      </h2>

      <mat-dialog-content>
        <div class="notifications-container">
          <mat-card
            *ngFor="let notification of notifications"
            class="notification-card"
            [class.unread]="!notification.read"
          >
            <mat-card-header>
              <mat-icon
                mat-card-avatar
                [class]="notification.type"
              >
                {{ getNotificationIcon(notification.type) }}
              </mat-icon>
              <mat-card-title>{{ notification.title }}</mat-card-title>
              <mat-card-subtitle>
                {{ notification.timestamp | date: 'medium' }}
              </mat-card-subtitle>
            </mat-card-header>

            <mat-card-content>
              <p class="notification-message">{{ notification.message }}</p>
            </mat-card-content>

            <mat-card-actions align="end">
              <button
                mat-raised-button
                color="primary"
                (click)="resolveJob(notification)"
                *ngIf="notification.type === 'issue'"
              >
                <mat-icon>build</mat-icon>
                Resolve Job
              </button>
              <button
                mat-button
                color="primary"
                (click)="markAsRead(notification)"
                *ngIf="notification.type !== 'issue'"
              >
                Mark as Read
              </button>
            </mat-card-actions>
          </mat-card>

          <div
            *ngIf="notifications.length === 0 && !(notificationStateService.isLoading$ | async)"
            class="no-notifications"
          >
            <mat-icon>notifications_none</mat-icon>
            <p>No notifications at this time</p>
          </div>

          <div
            *ngIf="notificationStateService.isLoading$ | async"
            class="loading-notifications"
          >
            <mat-spinner diameter="40"></mat-spinner>
            <p>Loading notifications...</p>
          </div>
        </div>
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button
          mat-button
          color="primary"
          (click)="markAllAsRead()"
          [disabled]="notifications.length === 0"
          >Mark All as Read</button
        >
        <button
          mat-button
          (click)="close()"
          >Close</button
        >
      </mat-dialog-actions>
    </div>
  `,
  styles: [
    `
      .notification-dialog {
        min-width: 500px;
      }

      h2 {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #1a237e;
        position: relative;
      }

      .notification-icon {
        color: #1a237e;
      }

      .refresh-btn {
        margin-left: auto;
      }

      .notifications-container {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 8px 0;
      }

      .notification-card {
        border-left: 4px solid transparent;
        transition: all 0.3s ease;
      }

      .notification-card.unread {
        border-left-color: #1a237e;
        background-color: #f5f5f5;
      }

      .info {
        color: #1976d2;
      }

      .warning {
        color: #ff9800;
      }

      .error {
        color: #f44336;
      }

      .issue {
        color: #e91e63;
      }

      .priority {
        color: #9c27b0;
      }

      .dispatch {
        color: #ff5722;
      }

      .no-notifications, .loading-notifications {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 32px;
        color: #666;
        text-align: center;
      }

      .no-notifications mat-icon, .loading-notifications mat-spinner {
        margin-bottom: 16px;
      }

      .no-notifications mat-icon {
        font-size: 48px;
        width: 48px;
        height: 48px;
      }

      .no-notifications p, .loading-notifications p {
        margin: 0;
        font-size: 16px;
      }

      mat-card-actions {
        padding: 8px;
      }

      .notification-message {
        white-space: pre-line;
        margin: 8px 0;
        line-height: 1.4;
      }

      mat-card-actions button mat-icon {
        margin-right: 4px;
      }
    `,
  ],
})
export class NotificationDialogComponent implements OnInit, OnDestroy {
  notifications: Notification[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<NotificationDialogComponent>,
    public notificationStateService: NotificationStateService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    // Subscribe to combined notifications from the shared state service
    this.notificationStateService.allNotifications$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ issues, priorityNotifications, dispatchAlerts }) => {
        const issueNotifications = this.transformJobIssuesToNotifications(issues);
        const priorityNotificationsList = this.transformPriorityNotifications(priorityNotifications);
        const dispatchAlertsList = this.transformDispatchAlerts(dispatchAlerts);

        // Combine and sort by timestamp (newest first)
        this.notifications = [...issueNotifications, ...priorityNotificationsList, ...dispatchAlertsList]
          .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        
        // Trigger change detection to ensure UI updates
        this.cdr.markForCheck();
      });

    // Listen for issue resolution events and refresh immediately
    this.notificationStateService.issueResolved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // When an issue is resolved, refresh the issues list
        this.notificationStateService.refreshIssues().subscribe();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  refreshNotifications() {
    this.notificationStateService.refreshIssues().subscribe();
  }

  resolveJob(notification: Notification) {
    if (notification.issueData) {
      const dialogRef = this.dialog.open(ResolveJobIssueDialogComponent, {
        width: '600px',
        data: notification.issueData
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          // Issue was resolved successfully
          console.log('Job issue resolved successfully');
        }
      });
    }
  }

  private transformJobIssuesToNotifications(issues: JobStageIssueResponse[]): Notification[] {
    return issues.map(issue => ({
      id: issue.id.toString(),
      title: 'Job Issue ' + issue.serialNumber,
      message: `Job Item: ${issue.jobItemName}\nIssue: ${issue.issueDescription}`,
      type: 'issue' as const,
      timestamp: new Date(issue.createdDate),
      read: false,
      issueData: issue // Store the original issue data for resolving
    }));
  }

  private transformPriorityNotifications(notifications: JobPriorityNotificationResponse[]): Notification[] {
    return notifications.map(notification => ({
      id: notification.id,
      title: `Priority Update - ${notification.jobNumber}`,
      message: `Customer: ${notification.customerName}\nNew Priority: ${notification.newPriority}${notification.dueDate ? '\nDue Date: ' + notification.dueDate.toLocaleDateString() : ''}`,
      type: 'priority' as const,
      timestamp: notification.timestamp,
      read: notification.read,
      priorityData: notification // Store the original priority data
    }));
  }

  private transformDispatchAlerts(notifications: DispatchAlertNotificationResponse[]): Notification[] {
    return notifications.map(notification => ({
      id: notification.id,
      title: `Dispatch Alert - ${notification.jobNumber}`,
      message: `Customer: ${notification.customerName}\nAlert Type: ${notification.alertType}\nDue Time: ${notification.dueTime}\nFreight Method: ${notification.freightMethod}\n\n${notification.message}`,
      type: 'dispatch' as const,
      timestamp: notification.timestamp,
      read: notification.read,
      dispatchData: notification // Store the original dispatch data
    }));
  }

  getNotificationIcon(type: 'info' | 'warning' | 'error' | 'issue' | 'priority' | 'dispatch'): string {
    const icons: { [key in 'info' | 'warning' | 'error' | 'issue' | 'priority' | 'dispatch']: string } = {
      info: 'info',
      warning: 'warning',
      error: 'error',
      issue: 'build',
      priority: 'priority_high',
      dispatch: 'local_shipping'
    };
    return icons[type] || 'notifications';
  }

  markAsRead(notification: Notification) {
    notification.read = true;

    // Also mark as read in the state service
    if (notification.type === 'priority' && notification.priorityData) {
      this.notificationStateService.markPriorityNotificationAsRead(notification.id);
    } else if (notification.type === 'dispatch' && notification.dispatchData) {
      this.notificationStateService.markDispatchAlertAsRead(notification.id);
    }
  }

  markAllAsRead() {
    this.notifications.forEach((notification) => {
      notification.read = true;
    });

    // Also mark all notifications as read in the state service
    this.notificationStateService.markAllPriorityNotificationsAsRead();
    this.notificationStateService.markAllDispatchAlertsAsRead();
  }

  close() {
    this.dialogRef.close();
  }
}
